import React, { useCallback, useEffect, useState } from 'react';
import * as actions from '../../actions';
import claimDocumentProjections from '../../projections/claimDocument';
import ClaimDocumentList from '../../components/claimDocuments/claimDocumentList';
import GENERAL_FUNCTIONS from '../../utils/functions/functions';
import usePagination from '../../hooks/usePagination';
import moment from 'moment';
import codes from '../../utils/constants/codes';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import useStateIfMounted from '../../hooks/useStateIfMounted';
import { ObjectOfAny } from '../../reducers/types';

type Props = {
    claimApplication: any;
};
const ClaimDocumentContainer = (props: Props): JSX.Element => {
    const { claimApplication } = props;
    const dispatch = useAppDispatch();
    const [isLoadingDownload, setLoadingDownload] = useState<null | string>(
        null,
    );
    const claimDocument = useAppSelector(
        claimDocumentProjections.documentList,
    ) as ObjectOfAny[];
    const claimDocumentLoader = useAppSelector(
        state => state.claimDocument.loading,
    );
    const pagination = useAppSelector(state => state.claimDocument.pagination);
    const [progressFileDownload, setProgressFileDownload] = useStateIfMounted(
        [],
    ) as any[];
    const fetchClaimDocumentWithQueryParamsAction = useCallback(
        (payload: actions.fetchClaimDocumentWithQueryParamsAction) =>
            dispatch(actions.fetchClaimDocumentWithQueryParams(payload)),
        [dispatch],
    );

    const {
        pagerState,
        goAtPrevious,
        goAtNext,
        goAtEnd,
        goAtStart,
        goAtPage,
        setState,
        setFiltersState,
        clearFilterState,
    } = usePagination();

    useEffect(() => {
        if (pagerState.isFetching) {
            const submittedByAragUserOnlyValue =
                pagerState.filters &&
                pagerState.filters['submittedByAragUserOnly']
                    ? pagerState.filters['submittedByAragUserOnly'] !==
                      codes.claimant
                    : null;
            fetchClaimDocumentWithQueryParamsAction({
                claimApplicationId: claimApplication.id,
                filters: {
                    page: pagerState.page,
                    limit: pagerState.limit,
                    submittedByAragUserOnly:
                        submittedByAragUserOnlyValue !== null
                            ? submittedByAragUserOnlyValue.toString()
                            : null,
                    submissionDateStart:
                        pagerState.filters &&
                        pagerState.filters['submissionDateStart']
                            ? moment(
                                  pagerState.filters['submissionDateStart'],
                              ).format('YYYY-MM-DD')
                            : null,
                    submissionDateEnd:
                        pagerState.filters &&
                        pagerState.filters['submissionDateEnd']
                            ? moment(
                                  pagerState.filters['submissionDateEnd'],
                              ).format('YYYY-MM-DD')
                            : null,
                    attachmentFullFileNameLike:
                        pagerState.filters &&
                        pagerState.filters['attachmentFullFileNameLike']
                            ? pagerState.filters['attachmentFullFileNameLike']
                            : null,
                },
            });
        }
    }, [pagerState.page, pagerState.limit, pagerState.filters]);

    useEffect(() => {
        if (pagination) {
            setState(pagination);
        }
    }, [pagination]);

    const handleFormReset = (formikResetForm: () => void) => () => {
        formikResetForm();
        clearFilterState();
        fetchClaimDocumentWithQueryParamsAction({
            claimApplicationId: claimApplication.id,
            filters: {
                page: pagerState.page,
                limit: pagerState.limit,
            },
        });
    };

    const handleFormSubmit = (formValues: {
        submissionDateStart: moment.MomentInput;
        submissionDateEnd: moment.MomentInput;
        submittedByAragUserOnly: any;
        attachmentFullFileNameLike: any;
    }) => {
        setFiltersState({
            submissionDateStart: formValues.submissionDateStart
                ? moment(formValues.submissionDateStart).format('YYYY-MM-DD')
                : null,
            submissionDateEnd: formValues.submissionDateEnd
                ? moment(formValues.submissionDateEnd).format('YYYY-MM-DD')
                : null,
            submittedByAragUserOnly: formValues.submittedByAragUserOnly,
            attachmentFullFileNameLike: formValues.attachmentFullFileNameLike,
        });
    };

    const clickDownloadButton = async (
        id: string,
        name: string | undefined | null,
        type: string,
    ) => {
        // setLoadingDownload(id);
        // await GENERAL_FUNCTIONS.requestToDownloadFile(id, fullFileName, type);
        // setLoadingDownload(null);
        setLoadingDownload(id);

        const blob = await GENERAL_FUNCTIONS.requestToDownloadFile(
            id,
            name,
            type,
            (id: any, progress: any) => {
                setProgressFileDownload((prevProgress: any[]) => {
                    // Find the index of the progress object with the matching fileId
                    const index = prevProgress.findIndex(
                        (item: { id: string }) => item.id === id,
                    );
                    // Create a new progress object with the updated progress value
                    const updatedProgress = { id: id, progress: progress };
                    // Update the progress array by replacing the old object with the updated one,
                    // or appending the updated object if it doesn't exist yet
                    if (index !== -1) {
                        return [
                            ...prevProgress.slice(0, index),
                            updatedProgress,
                            ...prevProgress.slice(index + 1),
                        ];
                    } else {
                        return [...prevProgress, updatedProgress];
                    }
                });
            },
        );

        if (blob && blob !== codes.warnings && blob !== codes.fileNotFound) {
            GENERAL_FUNCTIONS.downloadURI(blob, name as string);
            setLoadingDownload(null);
            setProgressFileDownload((prevProgress: any[]) => [
                ...prevProgress.filter(
                    (item: { id: string }) => item.id !== id,
                ),
            ]);
        }
        if (blob === codes.warnings) {
            setProgressFileDownload((prevProgress: any[]) => [
                ...prevProgress.filter(
                    (item: { id: string }) => item.id !== id,
                ),
            ]);
            setLoadingDownload(null);
        }
        if (blob === codes.fileNotFound) {
            setLoadingDownload(null);
        }
    };

    return (
        <ClaimDocumentList
            claimDocument={claimDocument}
            claimDocumentLoader={claimDocumentLoader}
            clickDownloadButton={clickDownloadButton}
            goAtPage={goAtPage}
            goAtStart={goAtStart}
            goAtEnd={goAtEnd}
            goAtNext={goAtNext}
            goAtPrevious={goAtPrevious}
            pager={pagerState}
            handleFormReset={handleFormReset}
            handleFormSubmit={handleFormSubmit}
            isLoadingDownload={isLoadingDownload}
            progressFileDownload={progressFileDownload}
            isDisabled={claimApplication.isAnonymised}
        />
    );
};

export default ClaimDocumentContainer;
